/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useEffect, useState } from 'react';
import { checkInteractions } from '@/lib/prescriptionExtras';

const EMPTY_INTERACTIONS = { findings: [], counts: {}, currentTherapy: [], evaluated: false, loading: false };

/**
 * usePrescriptionChecks — derives safety signals for a prescription draft:
 *  - `duplicates`: { itemIndex: activePrinciple } for drugs already in therapy
 *  - `interactions`: debounced server-side interaction check result
 *
 * Extracted from PrescriptionCreator.jsx (Code Quality refactor).
 */
export const usePrescriptionChecks = (items, patientId, currentTherapyActives) => {
  const [duplicates, setDuplicates] = useState({});
  const [interactions, setInteractions] = useState(EMPTY_INTERACTIONS);

  // Recompute duplicate flags whenever items or current therapy change.
  useEffect(() => {
    const dup = {};
    const actives = new Set((currentTherapyActives || []).map((a) => (a || '').toLowerCase()));
    items.forEach((it, idx) => {
      const cand = (it.active || it.name || '').toLowerCase().trim();
      if (!cand) return;
      // Match if active principle already in therapy, or if fuzzy match on
      // the free-text name against any existing active
      const hit = [...actives].find((a) => a && (cand === a || cand.includes(a) || a.includes(cand)));
      if (hit) dup[idx] = hit;
    });
    setDuplicates(dup);
  }, [items, currentTherapyActives]);

  // Debounced interaction check: whenever the drug list or patient changes.
  useEffect(() => {
    const names = items.map((it) => (it.name || '').trim()).filter(Boolean);
    if (names.length === 0) {
      setInteractions(EMPTY_INTERACTIONS);
      return undefined;
    }
    setInteractions((prev) => ({ ...prev, loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await checkInteractions(names, patientId || null);
        setInteractions({
          findings: res.findings || [],
          counts: res.counts || {},
          currentTherapy: res.current_therapy || [],
          evaluated: true,
          loading: false,
        });
      } catch {
        setInteractions({ ...EMPTY_INTERACTIONS, evaluated: true });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [items, patientId]);

  return { duplicates, interactions };
};
